import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';

import type {
  MediaAttachmentsOwnerContractDto,
  MediaOwnerAttachmentsReplaceRequest,
  MediaOwnerAttachmentsReplaceResponseDto,
} from '@core/api/contracts';
import type { MediaOwnerType, MediaRoleCode } from './models/media-browser.view-model';
import { HTTP_ERROR_FEEDBACK_POLICY } from '@core/interceptors/http-error-feedback-policy.token';
import { environment } from '../../../environments/environment';
import { toMediaAttachmentsOwnerViewModel } from './mappers/media-attachments-owner.mapper';
import type { MediaAttachmentsOwnerViewModel } from './models/media-attachments-owner.view-model';

@Injectable({ providedIn: 'root' })
export class MediaAttachmentsApiClient {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = resolveApiUrl();

  getOwnerAttachments(
    ownerType: MediaOwnerType,
    ownerId: number,
    roles?: MediaRoleCode[]
  ): Observable<MediaAttachmentsOwnerViewModel> {
    let params = new HttpParams()
      .set('ownerType', ownerType)
      .set('ownerId', ownerId);
    for (const role of roles ?? []) {
      params = params.append('roles', role);
    }

    return this.http.get<MediaAttachmentsOwnerContractDto>(
      `${this.apiUrl}/api/v1/media/attachments`,
      {
        context: featureOwnedContext(),
        params,
      }
    ).pipe(map(toMediaAttachmentsOwnerViewModel));
  }

  replaceOwnerAttachments(
    request: MediaOwnerAttachmentsReplaceRequest
  ): Observable<MediaOwnerAttachmentsReplaceResponseDto> {
    return this.http.put<MediaOwnerAttachmentsReplaceResponseDto>(
      `${this.apiUrl}/api/v1/media/attachments`,
      request,
      {
        context: featureOwnedContext(),
        headers: jsonHeaders(),
      }
    );
  }
}

function resolveApiUrl(): string {
  const baseUrl = environment.apiUrl || 'http://localhost:8080';
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
}

function featureOwnedContext(): HttpContext {
  return new HttpContext().set(HTTP_ERROR_FEEDBACK_POLICY, 'feature-owned');
}

function jsonHeaders(): HttpHeaders {
  return new HttpHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });
}
